import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, PlayCircle, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BRAND } from "@/lib/data";
import { useEffect } from "react";

/**
 * Premium cinematic 3D hero with:
 * - Aurora light beams + starfield
 * - Mouse-follow parallax on the car
 * - Rotating chrome rings, glass badges
 * - Perspective tilt as the mouse moves across the section
 */
export default function Hero3D({ onEnquire }) {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);

  const rotY = useSpring(useTransform(mx, [-1, 1], [-12, 12]), { stiffness: 60, damping: 20 });
  const rotX = useSpring(useTransform(my, [-1, 1], [8, -8]), { stiffness: 60, damping: 20 });
  const carShiftX = useSpring(useTransform(mx, [-1, 1], [-20, 20]), { stiffness: 90, damping: 22 });
  const carShiftY = useSpring(useTransform(my, [-1, 1], [-14, 14]), { stiffness: 90, damping: 22 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const nx = ((e.clientX - r.left) / r.width) * 2 - 1;
    const ny = ((e.clientY - r.top) / r.height) * 2 - 1;
    mx.set(nx);
    my.set(ny);
  };
  const onLeave = () => { mx.set(0); my.set(0); };

  // Generate stable star positions
  const stars = Array.from({ length: 40 }).map((_, i) => ({
    left: `${(i * 37) % 100}%`,
    top: `${(i * 53) % 100}%`,
    delay: `${(i * 0.17) % 4}s`,
    size: `${1 + ((i * 7) % 3)}px`,
  }));

  return (
    <section
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      className="relative min-h-[92vh] overflow-hidden bg-[#050B24] flex items-center"
      data-testid="hero-section"
      style={{ perspective: "1200px" }}
    >
      {/* Aurora light beams */}
      <div className="aurora" />

      {/* Diagonal moving light beam */}
      <div className="light-beam" style={{ animationDelay: "0s" }} />
      <div className="light-beam" style={{ animationDelay: "6s" }} />

      {/* Grid texture layer */}
      <div className="absolute inset-0 grid-bg opacity-70 z-[1]" />

      {/* Horizontal scan line */}
      <div className="scan-line" />

      {/* Star particles */}
      <div className="stars z-[1]">
        {stars.map((s, i) => (
          <span
            key={i}
            style={{
              left: s.left,
              top: s.top,
              width: s.size,
              height: s.size,
              animationDelay: s.delay,
            }}
          />
        ))}
      </div>

      {/* Speed lines */}
      <div className="absolute inset-0 pointer-events-none opacity-40 z-[2]">
        <div className="absolute top-1/4 left-0 w-32 h-px bg-gradient-to-r from-transparent via-red-500 to-transparent speed-line" style={{ animationDelay: "0s" }} />
        <div className="absolute top-1/2 left-0 w-24 h-px bg-gradient-to-r from-transparent via-white to-transparent speed-line" style={{ animationDelay: "1.2s" }} />
        <div className="absolute top-3/4 left-0 w-40 h-px bg-gradient-to-r from-transparent via-blue-400 to-transparent speed-line" style={{ animationDelay: "2s" }} />
      </div>

      <div className="relative z-10 max-w-7xl w-full mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
        {/* Left: content */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 border border-red-500/40 bg-red-500/10 rounded-full px-4 py-1.5 text-red-300 text-xs font-mono-brand tracking-[0.28em] uppercase mb-6 shadow-[0_0_30px_rgba(220,38,38,0.35)]"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-red-400 shadow-[0_0_10px_rgba(248,113,113,0.9)] animate-pulse" />
            Thane West, Maharashtra
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-display font-black text-5xl md:text-6xl lg:text-7xl xl:text-[92px] leading-[0.92] tracking-tighter"
          >
            <span className="text-gradient-chrome block">Complete</span>
            <span className="text-gradient-red block">Commercial Vehicle</span>
            <span className="text-gradient-chrome block">Solutions</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-6 md:mt-8 text-base md:text-xl text-slate-300 leading-relaxed max-w-2xl"
          >
            {BRAND.name} is your one-stop partner for T-Permit (Yellow Number
            Plate) vehicles — sales, finance, RTO registration, insurance,
            permit and airport attachment. All handled under one roof.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.45 }}
            className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
          >
            <Link to="/vehicles" data-testid="hero-explore-vehicles-btn">
              <Button
                size="lg"
                className="bg-red-600 hover:bg-red-700 text-white font-bold rounded-full px-8 h-14 text-base shadow-[0_20px_50px_rgba(220,38,38,0.5)] group relative overflow-hidden"
              >
                <span className="relative z-10 flex items-center">
                  Explore Vehicles
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </span>
              </Button>
            </Link>
            <Button
              size="lg"
              variant="outline"
              onClick={onEnquire}
              className="border-blue-400/40 bg-blue-500/10 text-blue-200 hover:bg-blue-500/20 hover:text-white hover:border-blue-400/60 rounded-full px-7 h-14 backdrop-blur"
              data-testid="hero-consultation-btn"
            >
              <PlayCircle className="w-5 h-5 mr-2" />
              Free Consultation
            </Button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-14 grid grid-cols-3 gap-6 max-w-lg border-t border-white/10 pt-8"
          >
            <StatChip value="500+" label="Vehicles Delivered" />
            <StatChip value="12+" label="Bank Partners" />
            <StatChip value="100%" label="RTO Support" />
          </motion.div>
        </div>

        {/* Right: 3D-style car showcase with mouse parallax */}
        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.85, rotateY: -20 }}
            animate={{ opacity: 1, scale: 1, rotateY: 0 }}
            transition={{ duration: 1.1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            style={{ perspective: "1200px", transformStyle: "preserve-3d" }}
            className="relative"
          >
            {/* Chrome ring 1 */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 28, repeat: Infinity, ease: "linear" }}
                className="w-[420px] h-[420px] rounded-full border border-red-500/30"
                style={{
                  background:
                    "conic-gradient(from 0deg, rgba(220,38,38,0.22), transparent 30%, rgba(30,64,175,0.28), transparent 70%, rgba(220,38,38,0.16))",
                }}
              />
            </div>
            {/* Chrome ring 2 (outer, counter-rotate) */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <motion.div
                animate={{ rotate: -360 }}
                transition={{ duration: 45, repeat: Infinity, ease: "linear" }}
                className="w-[560px] h-[560px] rounded-full border border-white/10"
                style={{
                  background:
                    "conic-gradient(from 90deg, rgba(226,232,240,0.15), transparent 40%, rgba(226,232,240,0.15) 80%, transparent)",
                }}
              />
            </div>

            {/* Car with 3D tilt driven by mouse */}
            <motion.div
              style={{
                rotateY: rotY,
                rotateX: rotX,
                x: carShiftX,
                y: carShiftY,
                transformStyle: "preserve-3d",
              }}
              className="relative float-car"
            >
              <div className="rounded-3xl overflow-hidden border border-white/15 shadow-[0_50px_120px_rgba(30,64,175,0.35)] shine-sweep bg-gradient-to-br from-slate-900/80 to-slate-950/80 backdrop-blur">
                <img
                  src="https://stimg.cardekho.com/images/carexteriorimages/930x620/Hyundai/Aura/10125/1762429751468/front-left-side-47.jpg"
                  alt="Hyundai Aura white sedan — T-Permit ready"
                  className="w-full h-auto relative z-10"
                />
              </div>
              {/* Ground shadow */}
              <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-[80%] h-8 bg-red-600/40 blur-3xl rounded-full" />
              <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 w-[70%] h-8 bg-blue-500/40 blur-3xl rounded-full" />
            </motion.div>

            {/* Floating badges */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1, duration: 0.6 }}
              className="absolute top-6 -left-4 md:left-0 rounded-2xl px-4 py-3 backdrop-blur-xl z-20 bg-gradient-to-br from-[#050B24]/95 to-[#0F1D45]/95 border border-red-500/50 shadow-[0_8px_32px_rgba(220,38,38,0.35)]"
            >
              <div className="text-[10px] uppercase tracking-widest text-red-400 font-mono-brand">Yellow Plate</div>
              <div className="text-white font-display font-bold text-sm mt-0.5">T-PERMIT READY</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.2, duration: 0.6 }}
              className="absolute bottom-6 -right-2 md:right-0 rounded-2xl px-4 py-3 backdrop-blur-xl z-20 bg-gradient-to-br from-[#050B24]/95 to-[#1E3A8A]/95 border border-blue-400/50 shadow-[0_8px_32px_rgba(30,64,175,0.35)]"
            >
              <div className="text-[10px] uppercase tracking-widest text-blue-300 font-mono-brand">Finance</div>
              <div className="text-white font-display font-bold text-sm mt-0.5">FROM 11% p.a.</div>
            </motion.div>

            {/* Small orbit marker */}
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
              className="absolute inset-0 flex items-center justify-center pointer-events-none"
            >
              <div className="w-[380px] h-[380px] relative">
                <span className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-red-500 shadow-[0_0_20px_rgba(220,38,38,0.9)]" />
                <span className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-2 h-2 rounded-full bg-blue-400 shadow-[0_0_20px_rgba(96,165,250,0.9)]" />
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Bottom marquee */}
      <div className="absolute bottom-0 left-0 right-0 border-t border-white/10 bg-black/50 backdrop-blur py-4 overflow-hidden z-10">
        <div className="marquee-track gap-16 whitespace-nowrap">
          {[...Array(2)].map((_, k) => (
            <div key={k} className="flex items-center gap-16 pr-16">
              {[
                "Hyundai Aura S CNG",
                "Maruti Dzire Tour S CNG",
                "Maruti Ertiga Tour M CNG",
                "Tata XPRES-T CNG",
                "Tata XPRES-T EV",
                "T-Permit Registration",
                "Airport Attachment",
                "Fleet Finance",
              ].map((item) => (
                <span
                  key={`${k}-${item}`}
                  className="text-slate-400 uppercase tracking-[0.28em] text-xs font-mono-brand"
                >
                  <span className="text-red-500">◆</span> {item}
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function StatChip({ value, label }) {
  return (
    <div className="count-up">
      <div className="font-display font-black text-3xl text-gradient-chrome">{value}</div>
      <div className="text-xs uppercase tracking-wider text-slate-400 mt-1">{label}</div>
    </div>
  );
}
